import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  CheckCircle2,
  ClipboardList,
  CreditCard,
  ExternalLink,
  ImagePlus,
  Link as LinkIcon,
  MessageCircle,
  QrCode,
  ShieldCheck,
  Sparkles,
  Store,
  TriangleAlert,
  Truck,
} from "lucide-react"
import Navbar from "../../components/LandPage/Navbar"
import Footer from "../../components/LandPage/Footer"
import LeadCaptureForm from "../../components/LandPage/LeadCaptureForm"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { useSignUpContext } from "../../context/useSignUpContext"
import { SignUp } from "../../Objects/SignUp/SignUp"
import { PATH_MAP } from "../../configuration/PathMap"
import { SEO } from "../../seo/Seo"
import { organizationSchema, softwareSchema } from "../../seo/schema"
import { trackMarketingEvent } from "../../analytics/tracking"
import { getLandingAnimationDelay } from "./landingPageUtils"
import { menuScreenshots } from "./menuScreenshots"
import {
  PublicAnimatedBackground,
  publicAnimatedBackgroundShellClassName,
} from "../../components/PublicAnimatedBackground/PublicAnimatedBackground"

type TextCard = { title: string; description: string }

const getDelay = (index: number) => getLandingAnimationDelay(index, 0.4)

const carouselIntervalMs = 3600

const featureIcons = [QrCode, Truck, CreditCard, MessageCircle, Store, ShieldCheck]

const supportIcons = [Store, QrCode, MessageCircle, CreditCard, Truck, LinkIcon]

export default function FreeMenuSetupLandingPage() {
  const { t } = useTranslation("seoLanding")

  const navigate = useNavigate()
  const { updateSignUp } = useSignUpContext()

  const keywords = t("free_menu_setup.keywords", { returnObjects: true }) as string[]
  const proofCards = t("free_menu_setup.proof_cards", { returnObjects: true }) as TextCard[]
  const problems = t("free_menu_setup.problems", { returnObjects: true }) as TextCard[]
  const solutions = t("free_menu_setup.solutions", { returnObjects: true }) as TextCard[]
  const benefits = t("free_menu_setup.benefits", { returnObjects: true }) as TextCard[]
  const appFeatures = t("digital_menu.conversion_features", { returnObjects: true }) as TextCard[]
  const supportItems = t("digital_menu.essential_items", { returnObjects: true }) as TextCard[]
  const valueItems = t("digital_menu.value_stack", { returnObjects: true }) as TextCard[]

  const [activeScreenshotIndex, setActiveScreenshotIndex] = useState(0)
  const [isScreenshotLoading, setIsScreenshotLoading] = useState(true)

  const activeScreenshot = useMemo(() => {
    if (menuScreenshots.length === 0) return null

    return menuScreenshots[activeScreenshotIndex % menuScreenshots.length]
  }, [activeScreenshotIndex])

  useEffect(() => {
    if (menuScreenshots.length <= 1) return

    const intervalId = window.setInterval(() => {
      setIsScreenshotLoading(true)
      setActiveScreenshotIndex((current) => (current + 1) % menuScreenshots.length)
    }, carouselIntervalMs)

    return () => window.clearInterval(intervalId)
  }, [])

  const handleLeadFocus = (target: string) => {
    trackMarketingEvent("landing_cta_click", { page: "free_menu_setup", target })
    document.getElementById("lead-capture")?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const handleCreateAccount = (target: string) => {
    trackMarketingEvent("landing_cta_click", { page: "free_menu_setup", target })
    updateSignUp(new SignUp())
    navigate(PATH_MAP.ROOT_SIGN_UP)
  }

  const handleViewExamples = (target: string) => {
    trackMarketingEvent("landing_cta_click", { page: "free_menu_setup", target })
    navigate(PATH_MAP.STORE_SEARCH_PAGE)
  }

  return (
    <div
      className={`${publicAnimatedBackgroundShellClassName} h-dvh overflow-x-hidden overflow-y-scroll bg-white text-slate-950 scrollbar-none`}
    >
      <PublicAnimatedBackground baseColor="#ffffff" />

      <SEO
        title={t("free_menu_setup.seo.title")}
        description={t("free_menu_setup.seo.description")}
        path={PATH_MAP.FREE_MENU_SETUP_LANDING_PAGE}
        keywords={keywords}
        structuredData={[organizationSchema, softwareSchema]}
      />

      <Animate type={AnimationTypes.slideDownIn} duration="0.9s">
        <Navbar />
      </Animate>

      <main className="mt-16 pb-20 pt-4 md:pb-0 lg:pt-8">
        <section className="relative overflow-hidden bg-white pt-3 lg:pt-6">
          <div className="mx-auto grid max-w-[1280px] gap-5 px-4 py-4 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:px-6 lg:py-12">
            <Animate type={AnimationTypes.slideRightIn} duration="1.15s">
              <div className="order-1 mx-auto w-full max-w-[260px] sm:max-w-[300px] lg:order-none lg:max-w-[430px]">
                <div className="relative mx-auto w-full max-w-[220px] rounded-[1.8rem] border border-slate-200 bg-slate-950 p-2 shadow-2xl shadow-slate-300/70 sm:max-w-[260px] lg:max-w-[360px] lg:rounded-[2.4rem] lg:p-3">
                  <div className="absolute left-1/2 top-2 z-10 h-4 w-20 -translate-x-1/2 rounded-b-2xl bg-slate-950 lg:top-3 lg:h-5 lg:w-24" />

                  <div className="relative aspect-[9/18.5] overflow-hidden rounded-[1.4rem] bg-slate-100 lg:rounded-[1.9rem]">
                    {activeScreenshot ? (
                      <>
                        <div
                          className={`absolute inset-0 z-10 bg-gradient-to-r from-slate-100 via-white to-slate-100 transition-opacity duration-500 ${
                            isScreenshotLoading ? "opacity-100" : "opacity-0"
                          }`}
                        >
                          <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/80 to-transparent animate-[shimmer_1.2s_infinite]" />
                        </div>

                        <Animate key={activeScreenshot.src} type={AnimationTypes.fadeInTraslate} duration="0.9s">
                          <img
                            src={activeScreenshot.src}
                            alt={`${t("free_menu_setup.hero_image_alt")} - ${activeScreenshot.alt}`}
                            className={`h-full w-full rounded-2xl object-cover p-1 transition-all duration-700 ${
                              isScreenshotLoading ? "scale-[1.02] opacity-0" : "scale-100 opacity-100"
                            }`}
                            loading={activeScreenshotIndex === 0 ? "eager" : "lazy"}
                            onLoad={() => setIsScreenshotLoading(false)}
                            onError={() => setIsScreenshotLoading(false)}
                          />
                        </Animate>

                        {menuScreenshots.length > 1 && (
                          <div className="absolute inset-x-5 bottom-3 z-20 flex gap-1.5 lg:inset-x-6 lg:bottom-4">
                            {menuScreenshots.map((item, index) => (
                              <button
                                key={item.src}
                                type="button"
                                aria-label={t("free_menu_setup.carousel_dot_aria", { index: index + 1 })}
                                onClick={() => {
                                  setIsScreenshotLoading(true)
                                  setActiveScreenshotIndex(index)
                                }}
                                className={`h-1.5 flex-1 rounded-full transition-colors ${
                                  index === activeScreenshotIndex ? "bg-white" : "bg-white/40"
                                }`}
                                data-interaction-description-key="interactionDescriptions:pages.landPage.freeMenuSetupLandingPage.button.carouselDotAria"
                              />
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center bg-linear-to-br from-orange-50 via-white to-emerald-50 p-7 text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-orange-600 shadow-lg shadow-orange-200/60">
                          <ImagePlus size={30} />
                        </div>

                        <p className="mt-5 text-center text-lg font-black leading-tight text-slate-950">
                          {t("free_menu_setup.preview_placeholder_title")}
                        </p>

                        <p className="mt-3 text-sm leading-relaxed text-slate-600">
                          {t("free_menu_setup.preview_placeholder_description")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mx-auto mt-2 hidden max-w-[260px] rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-center sm:block lg:mt-4 lg:max-w-[360px] lg:px-4 lg:py-3">
                  <p className="text-xs font-black text-emerald-800 lg:text-sm">
                    {t("free_menu_setup.visual_caption")}
                  </p>
                </div>
              </div>
            </Animate>

            <Animate type={AnimationTypes.slideLeftIn} duration="1.15s" delay="0.08s">
              <div className="order-2 lg:order-0">
                <span className="inline-flex items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1 text-xs font-black uppercase tracking-wide text-emerald-700">
                  {t("free_menu_setup.badge")}
                </span>

                <h1 className="mt-4 max-w-3xl text-lg font-black leading-tight text-slate-950 sm:text-xl lg:mt-5 lg:text-4xl">
                  {t("free_menu_setup.hero_title")}
                </h1>

                <div className="mt-5 flex flex-col gap-3 sm:flex-row lg:mt-7">
                  <button
                    type="button"
                    onClick={() => handleLeadFocus("hero_form")}
                    className="inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-6 py-3 text-sm font-black text-white shadow-lg shadow-red-500/20 transition hover:scale-[1.01] active:scale-95"
                    data-interaction-description-key="interactionDescriptions:pages.landPage.freeMenuSetupLandingPage.button.handleLeadFocusHeroForm"
                  >
                    <Sparkles size={18} />
                    {t("free_menu_setup.cta_primary")}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleCreateAccount("hero_create_account")}
                    className="inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-black text-slate-900 transition hover:bg-slate-50 active:scale-95"
                    data-interaction-description-key="interactionDescriptions:pages.landPage.freeMenuSetupLandingPage.button.handleCreateAccountHeroCreateAccount"
                  >
                    <ExternalLink size={18} />
                    {t("free_menu_setup.cta_secondary")}
                  </button>
                </div>

                <div className="mt-5 lg:mt-7">
                  <LeadCaptureForm
                    i18nPrefix="free_menu_setup_lead_form"
                    requireEmailField={false}
                    showEmailField={false}
                    showTrafficSourceField={false}
                    source="free_menu_setup_done_for_you"
                    trafficSourceFallback="Landing catálogo pronto gratis"
                    variant="compact"
                  />
                </div>

                <div className="mt-7 mb-2 grid gap-2 sm:grid-cols-2">
                  {proofCards.map((card, index) => (
                    <Animate
                      key={card.title}
                      type={AnimationTypes.fadeInTraslate}
                      duration="1s"
                      delay={getDelay(index)}
                    >
                      <article className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <h2 className="text-sm font-black text-slate-950">{card.title}</h2>
                        <p className="mt-1 text-xs leading-relaxed text-slate-600">{card.description}</p>
                      </article>
                    </Animate>
                  ))}
                </div>
              </div>
            </Animate>
          </div>
        </section>

        <section className="bg-slate-50 py-14">
          <div className="mx-auto max-w-[1280px] px-6">
            <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
              <div className="mx-auto max-w-3xl text-center">
                <span className="text-xs font-black uppercase tracking-wide text-red-600">
                  {t("free_menu_setup.problems_badge")}
                </span>
                <h2 className="mt-2 text-3xl font-black text-slate-950">{t("free_menu_setup.problems_title")}</h2>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">
                  {t("free_menu_setup.problems_description")}
                </p>
              </div>
            </Animate>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {problems.map((problem, index) => (
                <Animate key={problem.title} type={AnimationTypes.slideUpIn} duration="1.1s" delay={getDelay(index)}>
                  <article className="rounded-xl border border-red-100 bg-white p-5 shadow-sm">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-600">
                      <TriangleAlert size={19} />
                    </span>
                    <h3 className="mt-4 text-lg font-black text-slate-950">{problem.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{problem.description}</p>
                  </article>
                </Animate>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-950 py-14 text-white">
          <div className="mx-auto grid max-w-[1280px] gap-8 px-6 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
            <Animate type={AnimationTypes.slideRightIn} duration="1.05s">
              <div>
                <span className="text-xs font-black uppercase tracking-wide text-orange-300">
                  {t("free_menu_setup.solutions_badge")}
                </span>
                <h2 className="mt-2 text-3xl font-black leading-tight">{t("free_menu_setup.solutions_title")}</h2>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">
                  {t("free_menu_setup.solutions_description")}
                </p>

                <button
                  type="button"
                  onClick={() => handleLeadFocus("solutions_form")}
                  className="mt-6 inline-flex min-h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-6 py-3 text-sm font-black text-white shadow-lg shadow-red-500/20 transition hover:scale-[1.01] sm:w-auto"
                  data-interaction-description-key="interactionDescriptions:pages.landPage.freeMenuSetupLandingPage.button.handleLeadFocusSolutionsForm"
                >
                  <Sparkles size={18} />
                  {t("free_menu_setup.cta_primary")}
                </button>
              </div>
            </Animate>

            <div className="grid gap-4 md:grid-cols-3">
              {solutions.map((solution, index) => (
                <Animate key={solution.title} type={AnimationTypes.slideUpIn} duration="1.1s" delay={getDelay(index)}>
                  <article className="rounded-xl border border-white/10 bg-white/5 p-5">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-slate-950">
                      {index === 0 && <ClipboardList size={19} />}
                      {index === 1 && <QrCode size={19} />}
                      {index === 2 && <Sparkles size={19} />}
                    </span>
                    <h3 className="mt-4 text-lg font-black">{solution.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-300">{solution.description}</p>
                  </article>
                </Animate>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-14">
          <div className="mx-auto max-w-[1280px] px-6">
            <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
              <div className="mx-auto max-w-3xl text-center">
                <span className="text-xs font-black uppercase tracking-wide text-red-600">
                  {t("free_menu_setup.benefits_badge")}
                </span>
                <h2 className="mt-2 text-3xl font-black text-slate-950">{t("free_menu_setup.benefits_title")}</h2>
              </div>
            </Animate>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {benefits.map((benefit, index) => (
                <Animate
                  key={benefit.title}
                  type={AnimationTypes.fadeInTraslate}
                  duration="1.1s"
                  delay={getDelay(index)}
                >
                  <article className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                    <CheckCircle2 className="text-emerald-600" size={24} />
                    <h3 className="mt-4 text-lg font-black text-slate-950">{benefit.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{benefit.description}</p>
                  </article>
                </Animate>
              ))}
            </div>

            <Animate type={AnimationTypes.zoomIn} duration="1s" delay="0.15s">
              <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => handleLeadFocus("benefits_form")}
                  className="inline-flex min-h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-6 py-3 text-sm font-black text-white shadow-lg shadow-red-500/20 transition hover:scale-[1.01] sm:w-auto"
                  data-interaction-description-key="interactionDescriptions:pages.landPage.freeMenuSetupLandingPage.button.handleLeadFocusBenefitsForm"
                >
                  <MessageCircle size={18} />
                  {t("free_menu_setup.cta_primary")}
                </button>

                <button
                  type="button"
                  onClick={() => handleViewExamples("benefits_examples")}
                  className="inline-flex min-h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-black text-slate-900 transition hover:bg-slate-50 sm:w-auto"
                  data-interaction-description-key="interactionDescriptions:pages.landPage.freeMenuSetupLandingPage.button.handleViewExamplesBenefitsExamples"
                >
                  <ExternalLink size={18} />
                  {t("free_menu_setup.cta_examples")}
                </button>
              </div>
            </Animate>
          </div>
        </section>

        <section className="bg-slate-950 py-14 text-white">
          <div className="mx-auto max-w-[1280px] px-6">
            <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
              <div className="mx-auto max-w-3xl text-center">
                <span className="text-xs font-black uppercase tracking-wide text-orange-300">
                  {t("free_menu_setup.app_features_badge")}
                </span>
                <h2 className="mt-2 text-3xl font-black">{t("free_menu_setup.app_features_title")}</h2>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">
                  {t("free_menu_setup.app_features_description")}
                </p>
              </div>
            </Animate>

            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {appFeatures.map((feature, index) => {
                const Icon = featureIcons[index] ?? CheckCircle2

                return (
                  <Animate key={feature.title} type={AnimationTypes.zoomIn} duration="1.1s" delay={getDelay(index)}>
                    <article className="rounded-xl border border-white/10 bg-white/5 p-5">
                      <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white text-orange-600">
                        <Icon size={21} />
                      </div>
                      <h3 className="text-center font-black">{feature.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-slate-300">{feature.description}</p>
                    </article>
                  </Animate>
                )
              })}
            </div>
          </div>
        </section>

        <section className="bg-white py-14">
          <div className="mx-auto max-w-[1280px] px-6">
            <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
              <div className="mx-auto max-w-3xl text-center">
                <span className="text-xs font-black uppercase tracking-wide text-red-600">
                  {t("free_menu_setup.support_badge")}
                </span>
                <h2 className="mt-2 text-3xl font-black text-slate-950">{t("free_menu_setup.support_title")}</h2>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">
                  {t("free_menu_setup.support_description")}
                </p>
              </div>
            </Animate>

            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {supportItems.map((item, index) => {
                const Icon = supportIcons[index] ?? CheckCircle2

                return (
                  <Animate
                    key={item.title}
                    type={AnimationTypes.fadeInTraslate}
                    duration="1.1s"
                    delay={getDelay(index)}
                  >
                    <article className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                      <Icon className="text-red-600" size={22} />
                      <h3 className="mt-4 font-black text-slate-950">{item.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.description}</p>
                    </article>
                  </Animate>
                )
              })}
            </div>
          </div>
        </section>

        <section className="bg-orange-50 py-14">
          <div className="mx-auto max-w-[1280px] px-6">
            <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
              <div className="mx-auto max-w-3xl text-center">
                <span className="text-xs font-black uppercase tracking-wide text-orange-700">
                  {t("free_menu_setup.advantages_badge")}
                </span>
                <h2 className="mt-2 text-3xl font-black text-slate-950">{t("free_menu_setup.advantages_title")}</h2>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  {t("free_menu_setup.advantages_description")}
                </p>
              </div>
            </Animate>

            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {valueItems.map((item, index) => (
                <Animate key={item.title} type={AnimationTypes.slideUpIn} duration="1.1s" delay={getDelay(index)}>
                  <article className="rounded-xl border border-orange-200 bg-white p-5 shadow-sm">
                    <CheckCircle2 className="text-emerald-600" size={24} />
                    <h3 className="mt-4 font-black text-slate-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.description}</p>
                  </article>
                </Animate>
              ))}
            </div>

            <Animate type={AnimationTypes.zoomIn} duration="1s" delay="0.15s">
              <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => handleLeadFocus("advantages_form")}
                  className="inline-flex min-h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-6 py-3 text-sm font-black text-white shadow-lg shadow-red-500/20 transition hover:scale-[1.01] sm:w-auto"
                  data-interaction-description-key="interactionDescriptions:pages.landPage.freeMenuSetupLandingPage.button.handleLeadFocusAdvantagesForm"
                >
                  <Sparkles size={18} />
                  {t("free_menu_setup.cta_primary")}
                </button>

                <button
                  type="button"
                  onClick={() => handleCreateAccount("advantages_create_account")}
                  className="inline-flex min-h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-black text-slate-900 transition hover:bg-slate-50 sm:w-auto"
                  data-interaction-description-key="interactionDescriptions:pages.landPage.freeMenuSetupLandingPage.button.handleCreateAccountAdvantagesCreateAccount"
                >
                  <ExternalLink size={18} />
                  {t("free_menu_setup.cta_secondary")}
                </button>
              </div>
            </Animate>
          </div>
        </section>
      </main>

      <Animate type={AnimationTypes.slideUpIn} duration="1.1s" delay="0.2s">
        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-blue-200 bg-white/95 p-3 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] backdrop-blur md:hidden">
          <button
            type="button"
            onClick={() => handleCreateAccount("mobile_sticky_create")}
            className="flex min-h-12 w-full cursor-pointer items-center justify-center rounded-xl bg-blue-600 px-3 py-2 text-center text-sm font-black leading-tight text-white shadow-lg shadow-blue-500/25 transition active:scale-95"
            data-interaction-description-key="interactionDescriptions:pages.landPage.freeMenuSetupLandingPage.button.handleCreateAccountMobileStickyCreate"
          >
            {t("free_menu_setup.mobile_sticky_create")}
          </button>
        </div>
      </Animate>

      <Footer />
    </div>
  )
}
